import React, { useState } from "react";
import { Table, Spinner } from "react-bootstrap";
import { Paper } from "@material-ui/core";
import { useAlert } from "react-alert";
import { useSelector } from "react-redux";
import { baseUrl } from "../../../../services/config";

const CampusAttendanceSummary = () => {
  const authtoken = localStorage.getItem("userToken");
  const alert = useAlert();
  const user = useSelector((state) => state.user.user);
  const campuses = user.campuses || [];

  const today = new Date();
  const defaultMonth = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, "0")}`;

  const [selectedCampus, setSelectedCampus] = useState(campuses[0]?.id || "");
  const [salaryMonth, setSalaryMonth] = useState(defaultMonth);
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);

  const fetchSummary = async () => {
    if (!selectedCampus) {
      alert.show("Please select a campus.");
      return;
    }
    setLoading(true);
    setData(null);
    try {
      const params = new URLSearchParams({
        campus_id: selectedCampus,
        salary_month: salaryMonth + "-01",
      });
      const res = await fetch(
        `${baseUrl}/attendance/campus-monthly-summary?${params}`,
        { headers: { Authorization: `Bearer ${authtoken}` } }
      );
      const json = await res.json();
      if (json.metadata?.success) {
        setData(json.payload);
      } else {
        const msg = json.metadata?.message;
        const errorText =
          msg && typeof msg === "object"
            ? Object.values(msg).flat().join(" ")
            : typeof msg === "string"
            ? msg
            : "Failed to load summary.";
        alert.error(errorText);
      }
    } catch (err) {
      console.log(err);
      alert.error("Failed to fetch campus attendance summary.");
    } finally {
      setLoading(false);
    }
  };

  const isMissing = (emp) => emp.missing > 0;

  return (
    <Paper style={{ padding: 20, marginTop: 10 }}>
      <h4 className="mb-4">Campus Attendance Summary</h4>

      <div className="row mb-3 align-items-end">
        <div className="col-md-3">
          <label className="form-control-label"><b>Campus</b></label>
          <select
            className="form-control"
            value={selectedCampus}
            onChange={(e) => { setSelectedCampus(e.target.value); setData(null); }}
          >
            {campuses.map((c) => (
              <option key={c.id} value={c.id}>{c.name}</option>
            ))}
          </select>
        </div>

        <div className="col-md-2">
          <label className="form-control-label"><b>Month</b></label>
          <input
            type="month"
            className="form-control"
            value={salaryMonth}
            onChange={(e) => { setSalaryMonth(e.target.value); setData(null); }}
          />
        </div>

        <div className="col-md-2">
          <button
            onClick={fetchSummary}
            disabled={loading}
            className="btn btn-success button_blue w-100"
          >
            {loading ? <Spinner size="sm" animation="border" /> : "Fetch"}
          </button>
        </div>
      </div>

      {data && (
        <div
          style={{
            background: "#f8f9fa",
            borderRadius: 6,
            padding: "8px 16px",
            marginBottom: 14,
            display: "flex",
            gap: 24,
            flexWrap: "wrap",
            alignItems: "center",
          }}
        >
          <span><b>Campus:</b> {data.campus?.name}</span>
          <span><b>Month:</b> {data.salary_month}</span>
          <span><b>Employees:</b> {data.employees?.length ?? 0}</span>
        </div>
      )}

      {loading ? (
        <div className="text-center my-5">
          <Spinner animation="border" variant="primary" />
        </div>
      ) : data ? (
        <>
          <div className="mb-2" style={{ fontSize: 12, color: "#856404" }}>
            <span
              style={{
                display: "inline-block",
                width: 12,
                height: 12,
                background: "#fff3cd",
                border: "1px solid #ffc107",
                marginRight: 6,
                borderRadius: 2,
              }}
            />
            Highlighted rows: employee has missing attendance days (no biometric and no manual entry).
          </div>

          <Table responsive bordered size="sm">
            <thead className="tablehead">
              <tr className="tableheadtr text-center">
                <th className="background-color-primary tableHEadNAme">#</th>
                <th className="background-color-primary tableHEadNAme">Emp Code</th>
                <th className="background-color-primary tableHEadNAme">Name</th>
                <th className="background-color-primary tableHEadNAme">Designation</th>
                <th className="background-color-primary tableHEadNAme">Biometric</th>
                <th className="background-color-primary tableHEadNAme">Rule</th>
                <th className="background-color-primary tableHEadNAme">Total Days</th>
                <th className="background-color-primary tableHEadNAme">Off Days</th>
                <th className="background-color-primary tableHEadNAme">Working Days</th>
                <th className="background-color-primary tableHEadNAme">Marked Days</th>
                <th className="background-color-primary tableHEadNAme">Present</th>
                <th className="background-color-primary tableHEadNAme">Late</th>
                <th className="background-color-primary tableHEadNAme">Absent</th>
                <th className="background-color-primary tableHEadNAme">Leave</th>
                <th className="background-color-primary tableHEadNAme">Missing</th>
              </tr>
            </thead>
            <tbody>
              {!data.employees?.length ? (
                <tr>
                  <td colSpan={15} className="text-center text-muted py-4">
                    No employees found.
                  </td>
                </tr>
              ) : (
                data.employees.map((item, idx) => (
                  <tr
                    key={item.employee.id}
                    style={
                      isMissing(item)
                        ? { background: "#fff3cd" }
                        : undefined
                    }
                  >
                    <td className="text-center">{idx + 1}</td>
                    <td style={{ fontWeight: 500 }}>{item.employee.emp_code}</td>
                    <td>{item.employee.full_name}</td>
                    <td style={{ fontSize: 12 }}>{item.employee.designation || "—"}</td>
                    <td className="text-center">
                      {item.biometric_linked ? (
                        <span style={{ color: "#155724", fontWeight: 600 }}>✓</span>
                      ) : (
                        <span style={{ color: "#721c24", fontWeight: 600 }}>✗</span>
                      )}
                    </td>
                    <td className="text-center">
                      {item.attendance_rule_linked ? (
                        <span style={{ color: "#155724", fontWeight: 600 }}>✓</span>
                      ) : (
                        <span style={{ color: "#721c24", fontWeight: 600 }}>✗</span>
                      )}
                    </td>
                    <td className="text-center">{item.total_days}</td>
                    <td className="text-center">{item.off_days}</td>
                    <td className="text-center">{item.working_days}</td>
                    <td className="text-center">{item.marked_days}</td>
                    <td className="text-center">{item.present}</td>
                    <td className="text-center">{item.late_coming}</td>
                    <td className="text-center">{item.absent}</td>
                    <td className="text-center">{item.leave}</td>
                    <td className="text-center">{item.missing}</td>
                  </tr>
                ))
              )}
            </tbody>
          </Table>
        </>
      ) : (
        !loading && (
          <div className="text-center text-muted py-5">
            Select a campus and month, then click <b>Fetch</b>.
          </div>
        )
      )}
    </Paper>
  );
};

export default CampusAttendanceSummary;
